import {
  RECEIVE_POST_RETURN_INFO,
  RECEIVE_PRE_RETURN_INFO,
  RECEIVE_RETURN_LABEL_INFO,
  RECEIVE_RETURN_LABEL_PRODUCT_INFO,
  RESET_RETURNS_IN_STATE,
  RETURN_SUBMIT_COMPLETE,
  RETURN_SUBMIT_ERROR,
  RETURN_SUBMIT_INITIATE,
  STEP_SELECTION,
  UPDATE_BOX_INFO
} from 'constants/reduxActions';

import { productBundle } from 'apis/cloudcatalog';
import { getPrereturnInfo, placeReturn, returnLabelInfo } from 'apis/mafia';
import { prependAppRoot } from 'history/AppRootUtils';
import { authenticationErrorCatchHandlerFactory, err, setError } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';

export function receivePrereturnInfo(payload) {
  return {
    type: RECEIVE_PRE_RETURN_INFO,
    preReturnInfo: payload
  };
}

export function receivePostReturnInfo(payload) {
  return {
    type: RECEIVE_POST_RETURN_INFO,
    postReturnInfo: payload
  };
}

export function stepSelection(step) {
  return {
    type: STEP_SELECTION,
    step
  };
}

export function returnSubmitInitiate() {
  return {
    type: RETURN_SUBMIT_INITIATE
  };
}

export function returnSubmitComplete() {
  return {
    type: RETURN_SUBMIT_COMPLETE
  };
}

export function receiveReturnLabelInfo(labelInfo) {
  return {
    type: RECEIVE_RETURN_LABEL_INFO,
    labelInfo
  };
}

export function receiveReturnLabelProductInfo(productInfo) {
  return {
    type: RECEIVE_RETURN_LABEL_PRODUCT_INFO,
    productInfo
  };
}

export function returnSubmitError() {
  return {
    type: RETURN_SUBMIT_ERROR
  };
}

export function resetReturnsInState() {
  return {
    type: RESET_RETURNS_IN_STATE
  };
}

// Updating the total number of boxes to be used for return
export function updateBoxNumInfo(numOfBoxes, boxInfo = {}, items) {
  if (numOfBoxes === 1) { // reset box sorting; move all returnable items back to Box #1
    // need items to be deep copy otherwise original items array gets mutated after mapPrereturnInfoToBox :/
    boxInfo = { 1: { items: [...items] } };
  } else {
    let currentBoxesInStore = Object.keys(boxInfo).length;

    if (numOfBoxes > currentBoxesInStore) { // user wants to add more boxes
      for (let i = currentBoxesInStore + 1; i <= numOfBoxes; i++) {
        boxInfo[i] = { items: [] }; // add and initialize more boxes
      }
    }

    // remove extra boxes and move items (if customer placed any) back to Box #1
    let extraBoxKey = numOfBoxes;
    while (currentBoxesInStore > numOfBoxes) { // user changed their mind and wants to add fewer boxes
      const currentExtraBoxKey = ++extraBoxKey;
      boxInfo[1].items = boxInfo[1].items.concat(boxInfo[currentExtraBoxKey].items);
      delete boxInfo[currentExtraBoxKey];
      currentBoxesInStore = Object.keys(boxInfo).length;
    }
  }

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

// Selecting which items go into which box
export function updateItemInBoxInfo(boxNum, itemId, boxInfo) {
  let itemPlacedInBox = {};
  // loop through boxes in state and remove item from existing box.
  for (const box in boxInfo) {
    const { items } = boxInfo[box];
    const newItemsArr = [];

    items.forEach(item => {
      const { itemUniqueId: id } = item;
      if (id === itemId) {
        itemPlacedInBox = item; // save item
      } else {
        newItemsArr.push(item);
      }
    });
    boxInfo[box].items = newItemsArr;
  }
  boxInfo[boxNum].items.push(itemPlacedInBox); // place item in newly selected box

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

// Map response from return/initiate to box object in state
export function mapPrereturnInfoToBox(preReturnInfo, boxInfo) {
  const alreadyUsed = {};
  for (const box in boxInfo) { // loop through each box in store
    const { items } = boxInfo[box];

    items.forEach((item, i) => { // loop through items in current box
      if (item?.product) {
        const { product: { asin: itemAsin } } = item;

        preReturnInfo.forEach(order => {
          const { returnItems } = order;
          for (let j = 0; j < returnItems.length; j++) {
            const returnItem = returnItems[j];
            const { asin: boxItemAsin, unitId } = returnItem;
            if ((itemAsin === boxItemAsin) && !alreadyUsed[unitId]) { // if a match, replace item in store w/ preReturn info item
              items[i] = returnItem;
              items[i].reasonCode = '18'; // set default reason code to 18 (no reason given)
              alreadyUsed[unitId] = true;
              break;
            }
          }
        });
      }
    });
  }

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

export function updateBoxInfoWithReturnReason(unitId, reasonCode, boxInfo, boxNum) {
  const { items } = boxInfo[boxNum];

  items.forEach(item => {
    const { unitId: itemUnitId } = item;
    if (itemUnitId === unitId) {
      item.reasonCode = reasonCode;
    }
  });

  return {
    type: UPDATE_BOX_INFO,
    boxInfo
  };
}

export function fetchSpecificReturnInfo(orderId, orderAsins, fetchPrereturnInfoEndpoint = getPrereturnInfo) {
  return (dispatch, getState) => {
    const {
      cookies,
      environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions },
      returns: { boxInfo }
    } = getState();

    return fetchPrereturnInfoEndpoint(mafia, orderId, orderAsins, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        // Mafia returns null value for orderId on prereturn info so need to map it back in
        resp.returnItems.forEach(item => {
          item.orderId = item.orderId || orderId;
        });
        dispatch(receivePrereturnInfo(resp));
        const { returns: { preReturnInfo } } = getState();
        dispatch(mapPrereturnInfoToBox(preReturnInfo, boxInfo));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/returns', locationBeforeTransitions)));
  };
}

export function fetchAdditionalContractId(orderId, orderItemId, fetchPrereturnInfoEndpoint = getPrereturnInfo) {
  return (dispatch, getState) => {
    const {
      cookies,
      environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions }
    } = getState();

    return fetchPrereturnInfoEndpoint(mafia, orderId, orderItemId, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ contractId }) => contractId)
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/returns', locationBeforeTransitions)));
  };
}

export function submitReturn(returnInfo, submitReturnEndpoint = placeReturn) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return submitReturnEndpoint(mafia, returnInfo, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receivePostReturnInfo(response));
      })
      .catch(err => {
        if (err.status === 400) {
          throw err;
        } else {
          authenticationErrorCatchHandlerFactory(dispatch, '/return')(err);
        }
      });
  };
}

export function fetchReturnLabelInfo({ rmaId, contractId }, getReturnLabelInfo = returnLabelInfo, getProductBundle = productBundle) {
  return function (dispatch, getState) {
    if (!(rmaId || contractId)) {
      return dispatch(setError('Error loading return label-- no RMA or Contract ID.', null, 404));
    } else {
      const { cookies, environmentConfig: { api: { mafia, cloudcatalog } } } = getState();

      return getReturnLabelInfo(mafia, { rmaId, contractId }, cookies)
        .then(processHeadersMiddleware(setSessionCookies(dispatch)))
        .then(fetchApiNotAuthenticatedMiddleware)
        .then(fetchErrorMiddleware)
        .then(response => {
          dispatch(receiveReturnLabelInfo(response));

          const { asins } = response;
          if (asins?.length) {
            asins.forEach(asin => getProductBundle(cloudcatalog, { asin })
              .then(response => response.json())
              .then(response => {
                if (response.product?.length === 1) {
                  const product = { ...response.product[0], asin };
                  dispatch(receiveReturnLabelProductInfo(product));
                }
              }));
          }
        })
        .catch(e => {
          dispatch(setError(err.GENERIC, e));
        });
    }
  };
}
